/**
 * mSTEM SmartHome kits reducer (GEN-04). Holds the in-memory SmartHome kit
 * inventory surfaced by KitManagerModal. Seeded at module load from
 * src/lib/mstem-mock/smarthome-kits.js; can be replaced at runtime via
 * SEED_KITS (used when hydrating from localStorage on KitManagerModal mount).
 *
 * Security note (T-02-04-01, T-02-04-03): ADD_KIT and UPDATE_KIT validate the
 * incoming kit via `sanitizeKit` — empty/oversized name, missing id, negative
 * sensor/relay counts, counts above 999, or non-finite numeric input all
 * return the same state reference. hasRFID is coerced to boolean to prevent
 * non-bool injection (T-02-04-03).
 *
 * SMH-04 detail fields (firmwareVersion, model, mcu, hardwareRevision,
 * serialNumber, macAddress, ipAddress, activatedAt, lastSyncAt) survive the
 * sanitizer instead of being stripped — KitDetailModal reads them straight
 * off the reducer list. Each is coerced to a trimmed string capped at
 * MAX_DETAIL_LENGTH (non-strings → ''), and connectionStatus is whitelisted,
 * so a tampered localStorage entry still cannot inject objects or oversized
 * blobs into the store.
 */

import MOCK_KITS from '../lib/mstem-mock/smarthome-kits';

const ADD_KIT = 'scratch-gui/kits/ADD_KIT';
const UPDATE_KIT = 'scratch-gui/kits/UPDATE_KIT';
const DELETE_KIT = 'scratch-gui/kits/DELETE_KIT';
const SEED_KITS = 'scratch-gui/kits/SEED_KITS';

const MAX_NAME_LENGTH = 100;
const MAX_COUNT = 999;
// SMH-04 — cap for every free-text detail field (MAC, IP, model, serial…).
const MAX_DETAIL_LENGTH = 64;
const CONNECTION_STATUSES = ['connected', 'offline', 'outdated'];
const DEFAULT_CONNECTION_STATUS = 'offline';

const initialState = {
    list: MOCK_KITS.slice()
};

// SMH-04 — free-text detail coercion: non-strings become '' (never null/object)
// so KitDetailModal can render every row without per-field type checks.
const sanitizeDetail = function (value) {
    if (typeof value !== 'string') return '';
    return value.trim().substring(0, MAX_DETAIL_LENGTH);
};

const sanitizeKit = function (kit) {
    if (!kit) return null;
    const name = typeof kit.name === 'string' ? kit.name.trim() : '';
    if (!name || name.length > MAX_NAME_LENGTH) return null;
    if (typeof kit.id !== 'string' || kit.id.length === 0) return null;
    const sensorCount = Number(kit.sensorCount);
    const relayCount = Number(kit.relayCount);
    if (!Number.isFinite(sensorCount) || sensorCount < 0 || sensorCount > MAX_COUNT) return null;
    if (!Number.isFinite(relayCount) || relayCount < 0 || relayCount > MAX_COUNT) return null;
    const connectionStatus = CONNECTION_STATUSES.indexOf(kit.connectionStatus) === -1 ?
        DEFAULT_CONNECTION_STATUS :
        kit.connectionStatus;
    return {
        id: kit.id,
        name: name,
        sensorCount: Math.floor(sensorCount),
        relayCount: Math.floor(relayCount),
        hasRFID: Boolean(kit.hasRFID),
        image: kit.image || null,
        brand: kit.brand || 'MBF SmartHome',
        // SMH-04 detail block — preserved through add/edit round-trips.
        model: sanitizeDetail(kit.model),
        mcu: sanitizeDetail(kit.mcu),
        hardwareRevision: sanitizeDetail(kit.hardwareRevision),
        serialNumber: sanitizeDetail(kit.serialNumber),
        macAddress: sanitizeDetail(kit.macAddress),
        ipAddress: sanitizeDetail(kit.ipAddress),
        firmwareVersion: sanitizeDetail(kit.firmwareVersion),
        connectionStatus: connectionStatus,
        activatedAt: sanitizeDetail(kit.activatedAt),
        lastSyncAt: sanitizeDetail(kit.lastSyncAt)
    };
};

const reducer = function (state, action) {
    if (typeof state === 'undefined') state = initialState;
    switch (action.type) {
    case SEED_KITS:
        if (!Array.isArray(action.list)) return state;
        return Object.assign({}, state, {
            list: action.list.slice()
        });
    case ADD_KIT: {
        const sanitized = sanitizeKit(action.kit);
        if (!sanitized) return state;
        // Reject duplicate ids — preserves uniqueness for delete/edit by id.
        if (state.list.some(k => k.id === sanitized.id)) {
            return state;
        }
        return Object.assign({}, state, {
            list: state.list.concat([sanitized])
        });
    }
    case UPDATE_KIT: {
        const sanitized = sanitizeKit(action.kit);
        if (!sanitized) return state;
        return Object.assign({}, state, {
            list: state.list.map(k => (
                k.id === sanitized.id ? sanitized : k
            ))
        });
    }
    case DELETE_KIT:
        if (typeof action.id !== 'string') return state;
        return Object.assign({}, state, {
            list: state.list.filter(k => k.id !== action.id)
        });
    default:
        return state;
    }
};

const addKit = function (kit) {
    return {type: ADD_KIT, kit: kit};
};
const updateKit = function (kit) {
    return {type: UPDATE_KIT, kit: kit};
};
const deleteKit = function (id) {
    return {type: DELETE_KIT, id: id};
};
const seedKits = function (list) {
    return {type: SEED_KITS, list: list};
};

export {
    reducer as default,
    initialState as kitsInitialState,
    addKit,
    updateKit,
    deleteKit,
    seedKits
};
